/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import I18nContextComponent from '@oneaudi/i18n-context';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AudiMarketContextServiceV2 } from '@oneaudi/market-context-service';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { LayerManagerV27 } from '@volkswagen-onehub/layer-manager';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { TrackingContext } from './context';
import FeatureApp from './FeatureApp';
import type { PartnerId } from './types/partner-id';
import { ContentContextProvider } from './context/ContentContext';
import { getDesignLibrary } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'layer-manager': LayerManagerV27 | undefined;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 'audi-market-context-service': AudiMarketContextServiceV2 | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1;
  readonly 's2:logger': Logger;
  readonly 'audi-content-service': ContentServiceV1;
}

// Type to work with the microkernel that maybe is available globaly
declare global {
  interface Window {
    microkernel: {
      stateRegistry: {
        subscribeToStore: (name: string, callback: (state: PartnerId) => void) => void;
        unsubscribeFromStore: (name: string, callback: (state: PartnerId) => void) => void;
      };
    };
  }
}

export type AudiFooterConfig = {
  api?: string;
  enablePartnerIdReplacement?: boolean;
  hideSocialMedia?: boolean;
};

type FeatureHubAppDefinitionType = FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServiceDependencies,
  AudiFooterConfig
>;

const featureAppDefinition: FeatureHubAppDefinitionType = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'layer-manager': '^2.5.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '3.0.0',
    },
    externals: {
      react: '^17.0.2 || ^18.2.0',
      'styled-components': '^5.3.11',
      '@feature-hub/react': '^3',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-market-context-service': '^2.0.0',
    },
  },
  create: ({ baseUrl, featureServices, featureAppId }) => {
    const logger = featureServices['s2:logger'];
    logger?.info('Feature App created.', featureServices);

    if (!baseUrl) {
      throw new Error(`The feature app's baseUrl is undefined.`);
    }

    const { countryCode } = featureServices['gfa:locale-service'];
    const i18nData: I18NServiceV1 = featureServices['dbad:audi-i18n-service'];

    const referenceServiceScopeManager = featureServices[
      'audi-footnote-reference-service'
    ] as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
    const layerManager = featureServices['layer-manager'];
    const localeService = featureServices['locale-service'];
    const contentService = featureServices['audi-content-service'];
    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const referenceServiceManager = referenceServiceScopeManager
      ? referenceServiceScopeManager.getDefaultScopeRefService()
      : undefined;

    return {
      render: (): React.ReactNode => (
        <ThemeProvider theme={{ name: 'dark' }}>
          <ContentContextProvider contentService={contentService}>
            <I18nContextComponent
              /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              // @ts-ignore
              featureServices={featureServices}
              i18nData={i18nData}
              scopes={['nemo.common', 'fa.audi-basket']}
            >
              {/* @ts-expect-error optional dependency */}
              <TrackingContext.Provider value={trackingService}>
                <FeatureApp
                  localeService={localeService}
                  countryCode={countryCode}
                  featureAppId={featureAppId}
                  referenceServiceManager={referenceServiceManager}
                  layerManager={layerManager}
                  logger={logger}
                />
              </TrackingContext.Provider>
            </I18nContextComponent>
          </ContentContextProvider>
        </ThemeProvider>
      ),
    };
  },
};
export default featureAppDefinition;
